import { chartConfig } from '@config/chart';
import { brandColors } from '@utils/color-utils';

import { YScale } from './types';

// Laser track components that create traveling light effect
function LaserTrack({
  x,
  y,
  width,
  height,
  color,
  isActive = false,
  rx = 8,
  startOffset = 0,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  isActive?: boolean;
  rx?: number;
  startOffset?: number;
}) {
  const perimeter = 2 * (width + height);
  const animationDuration = isActive
    ? chartConfig.laser.animation.durationActive
    : chartConfig.laser.animation.durationInactive;
  const startPosition = startOffset * perimeter;

  // Create multiple overlapping segments for smooth gradient effect
  const gradientSteps = chartConfig.laser.gradientSteps;
  const baseSegmentLength = perimeter * chartConfig.laser.baseSegmentLength;

  return (
    <g>
      {Array.from({ length: gradientSteps }, (_, i) => {
        const stepRatio = (gradientSteps - i) / gradientSteps; // 1.0 to 0.125
        const segmentLength = baseSegmentLength * (1 + stepRatio * 2); // Varying lengths
        const opacity =
          stepRatio *
          (isActive
            ? chartConfig.laser.opacity.active
            : chartConfig.laser.opacity.inactive);
        const strokeWidth = Math.max(
          1,
          Math.round(
            stepRatio *
              (isActive
                ? chartConfig.laser.strokeWidth.active
                : chartConfig.laser.strokeWidth.inactive),
          ),
        );
        const offset = i * baseSegmentLength * chartConfig.laser.segmentOffset;

        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={width}
            height={height}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${segmentLength} ${perimeter - segmentLength}`}
            strokeLinecap="round"
            rx={rx}
            opacity={opacity}
            style={{
              pointerEvents: 'none',
              filter:
                i === 0
                  ? `drop-shadow(0 0 ${isActive ? chartConfig.laser.glow.active : chartConfig.laser.glow.inactive}px ${color})`
                  : 'none',
            }}
          >
            <animate
              attributeName="stroke-dashoffset"
              values={`${startPosition - offset}; ${startPosition - offset - perimeter}`}
              dur={animationDuration}
              repeatCount="indefinite"
            />
          </rect>
        );
      })}
    </g>
  );
}

interface TradeZonesProps {
  strikePrice: number;
  yScale: YScale;
  innerWidth: number;
  innerHeight: number;
  margin: { top: number; bottom: number };
  selectedTradeType?: 'CALL' | 'PUT' | null;
  onTradeZoneSelect?: (tradeType: 'CALL' | 'PUT') => void;
  onTradeZoneDeselect?: () => void;
  isDraggingStrike?: boolean;
  previewStrikePrice?: number | null;
}

export function TradeZones({
  strikePrice,
  yScale,
  innerWidth,
  innerHeight,
  margin,
  selectedTradeType,
  onTradeZoneSelect,
  onTradeZoneDeselect,
  isDraggingStrike = false,
  previewStrikePrice = null,
}: TradeZonesProps) {
  if (!strikePrice || strikePrice <= 0) {
    return null;
  }

  // Use preview strike price during drag for smooth following
  const effectiveStrikePrice =
    isDraggingStrike && previewStrikePrice ? previewStrikePrice : strikePrice;

  return (
    <g>
      {/* Green zone above strike (ABOVE/CALL area) */}
      {(!selectedTradeType || selectedTradeType === 'CALL') && (
        <>
          {/* Background fill */}
          <rect
            x={0}
            y={-margin.top}
            width={innerWidth}
            height={yScale(effectiveStrikePrice) + margin.top}
            fill={brandColors.green}
            opacity={
              selectedTradeType === 'CALL'
                ? chartConfig.tradeZones.opacity.active
                : chartConfig.tradeZones.opacity.inactive
            }
            rx={chartConfig.tradeZones.borderRadius}
            style={{ pointerEvents: 'none' }}
          />

          {/* Invisible clickable area */}
          <rect
            x={0}
            y={-margin.top}
            width={innerWidth}
            height={yScale(effectiveStrikePrice) + margin.top}
            fill="transparent"
            style={{
              pointerEvents: 'all',
              cursor: 'pointer',
            }}
            onClick={e => {
              if (selectedTradeType === 'CALL') {
                onTradeZoneDeselect?.();
              } else {
                onTradeZoneSelect?.('CALL');
              }
              e.stopPropagation();
            }}
            onTouchEnd={e => {
              if (selectedTradeType === 'CALL') {
                onTradeZoneDeselect?.();
              } else {
                onTradeZoneSelect?.('CALL');
              }
              e.stopPropagation();
            }}
            data-testid="trade-zone-call"
          />

          {/* Laser track border */}
          <LaserTrack
            x={0}
            y={-margin.top}
            width={innerWidth}
            height={yScale(effectiveStrikePrice) + margin.top}
            color={brandColors.green}
            isActive={selectedTradeType === 'CALL'}
            rx={chartConfig.tradeZones.borderRadius}
            startOffset={0}
          />
        </>
      )}

      {/* Red zone below strike (BELOW/PUT area) */}
      {(!selectedTradeType || selectedTradeType === 'PUT') && (
        <>
          {/* Background fill */}
          <rect
            x={0}
            y={yScale(effectiveStrikePrice)}
            width={innerWidth}
            height={Math.max(
              0,
              innerHeight + margin.bottom - yScale(effectiveStrikePrice),
            )}
            fill={brandColors.red}
            opacity={
              selectedTradeType === 'PUT'
                ? chartConfig.tradeZones.opacity.active
                : chartConfig.tradeZones.opacity.inactive
            }
            rx={chartConfig.tradeZones.borderRadius}
            style={{ pointerEvents: 'none' }}
          />

          {/* Invisible clickable area */}
          <rect
            x={0}
            y={yScale(effectiveStrikePrice)}
            width={innerWidth}
            height={Math.max(
              0,
              innerHeight + margin.bottom - yScale(effectiveStrikePrice),
            )}
            fill="transparent"
            style={{
              pointerEvents: 'all',
              cursor: 'pointer',
            }}
            onClick={e => {
              if (selectedTradeType === 'PUT') {
                onTradeZoneDeselect?.();
              } else {
                onTradeZoneSelect?.('PUT');
              }
              e.stopPropagation();
            }}
            onTouchEnd={e => {
              if (selectedTradeType === 'PUT') {
                onTradeZoneDeselect?.();
              } else {
                onTradeZoneSelect?.('PUT');
              }
              e.stopPropagation();
            }}
            data-testid="trade-zone-put"
          />

          {/* Laser track border */}
          <LaserTrack
            x={0}
            y={yScale(effectiveStrikePrice)}
            width={innerWidth}
            height={Math.max(
              0,
              innerHeight + margin.bottom - yScale(effectiveStrikePrice),
            )}
            color={brandColors.red}
            isActive={selectedTradeType === 'PUT'}
            rx={chartConfig.tradeZones.borderRadius}
            startOffset={0.6}
          />
        </>
      )}

      {/* Invisible overlay for ABOVE area when PUT is selected - switches to CALL */}
      {selectedTradeType === 'PUT' && (
        <rect
          x={0}
          y={-margin.top}
          width={innerWidth}
          height={yScale(effectiveStrikePrice) + margin.top}
          fill="transparent"
          style={{
            pointerEvents: 'all',
            cursor: 'pointer',
          }}
          onClick={e => {
            onTradeZoneSelect?.('CALL');
            e.stopPropagation();
          }}
          onTouchEnd={e => {
            onTradeZoneSelect?.('CALL');
            e.stopPropagation();
          }}
        />
      )}

      {/* Invisible overlay for BELOW area when CALL is selected - switches to PUT */}
      {selectedTradeType === 'CALL' && (
        <rect
          x={0}
          y={yScale(effectiveStrikePrice)}
          width={innerWidth}
          height={Math.max(
            0,
            innerHeight + margin.bottom - yScale(effectiveStrikePrice),
          )}
          fill="transparent"
          style={{
            pointerEvents: 'all',
            cursor: 'pointer',
          }}
          onClick={e => {
            onTradeZoneSelect?.('PUT');
            e.stopPropagation();
          }}
          onTouchEnd={e => {
            onTradeZoneSelect?.('PUT');
            e.stopPropagation();
          }}
        />
      )}
    </g>
  );
}
